import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Droplets, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { bloodworkApi } from "@/lib/consult/api";

const GREEN = "#19734a";
const TEAL = "#2f7d64";
const AMBER = "#b7791f";
const RED = "#b84a4a";
const MUTED = "#687385";
const BORDER = "#e3e6e1";

const CARD = {
  background: "#fff",
  border: `1px solid ${BORDER}`,
  borderRadius: 12,
  boxShadow: "0 1px 2px rgba(16,24,40,.05)",
};

function pctWidth(value) {
  return `${Math.max(0, Math.min(100, Number(value) || 0))}%`;
}

function markerBadge(status, type) {
  if (type === "benchmark") {
    if (status === "in_range") return { label: "Benchmark in", color: GREEN, bg: "#eaf5ef" };
    if (status === "outside_range") return { label: "Benchmark out", color: AMBER, bg: "#fff7ed" };
    if (status === "benchmark_not_ready") return { label: "Benchmark pending", color: MUTED, bg: "#f1f5f9" };
    return { label: "Not available", color: "#475569", bg: "#f1f5f9" };
  }
  if (status === "normal") return { label: "Normal", color: GREEN, bg: "#eaf5ef" };
  if (status === "below_normal") return { label: "Below normal", color: AMBER, bg: "#fff7ed" };
  if (status === "above_normal") return { label: "Above normal", color: AMBER, bg: "#fff7ed" };
  return { label: "Not available", color: "#475569", bg: "#f1f5f9" };
}

export default function BloodworkPanel({ isAdmin = false }) {
  const [view, setView] = useState("stats");
  const [month, setMonth] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [individualResult, setIndividualResult] = useState(null);
  const [individualLoading, setIndividualLoading] = useState(false);
  const bulkInputRef = useRef(null);
  const individualInputRef = useRef(null);

  const loadSummary = useCallback(async (selectedMonth = month) => {
    setLoading(true);
    try {
      const { data } = await bloodworkApi.get("/summary", { params: { month: selectedMonth } });
      setSummary(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load bloodwork statistics");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { loadSummary(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadBulk = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    if (!isAdmin) {
      toast.error("Only an admin can bulk-upload benchmark records");
      return;
    }
    setBulkProgress({ processed: 0, total: selected.length, stored: 0, failed: 0 });
    let latestSummary = null;
    let stored = 0;
    let failed = 0;
    try {
      const batchSize = 10;
      for (let start = 0; start < selected.length; start += batchSize) {
        const batch = selected.slice(start, start + batchSize);
        const form = new FormData();
        batch.forEach((file) => form.append("files", file));
        const { data } = await bloodworkApi.post("/upload?mode=bulk", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        latestSummary = data.summary;
        stored += data.stored || 0;
        failed += data.failed || 0;
        setBulkProgress({ processed: Math.min(start + batch.length, selected.length), total: selected.length, stored, failed });
      }
      if (latestSummary) setSummary(latestSummary);
      await loadSummary(month);
      toast.success(`Added ${stored} anonymous records${failed ? ` · ${failed} could not be parsed` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkProgress(null);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  };

  const uploadIndividual = async (file) => {
    if (!file) return;
    setIndividualLoading(true);
    try {
      const form = new FormData();
      form.append("files", file);
      const { data } = await bloodworkApi.post("/upload?mode=individual", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIndividualResult(data);
      toast.success("Report assessed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't assess this report");
    } finally {
      setIndividualLoading(false);
      if (individualInputRef.current) individualInputRef.current.value = "";
    }
  };

  const monthOptions = summary?.months || [];
  const labs = summary?.labs || [];
  const result = individualResult?.assessment;

  return (
    <div className="bw-panel" data-testid="bloodwork-panel">
      <style>{`
        .bw-panel { max-width: 1010px; margin: 0 auto; }
        .bw-head { min-height: 42px; display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 12px; }
        .bw-title { margin: 0; font-family: Outfit, sans-serif; font-size: 22px; font-weight: 500; color: #111827; }
        .bw-subnav { display: flex; gap: 8px; overflow-x: auto; margin-bottom: 12px; }
        .bw-subnav button { flex: 0 0 auto; border: 1px solid ${BORDER}; border-radius: 999px; background: #fff; color: ${MUTED}; padding: 7px 11px; font-size: 11px; font-weight: 800; }
        .bw-subnav button.active { background: #edf5f1; color: ${TEAL}; border-color: #cfe2da; }
        .bw-metrics { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; margin-bottom: 14px; }
        .bw-marker-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
        .bw-table { overflow-x: auto; }
        @media (max-width: 720px) {
          .bw-metrics { grid-template-columns: 1fr; }
          .bw-marker-grid { gap: 7px; }
        }
      `}</style>

      <div className="bw-head">
        <h2 className="bw-title" data-testid="bloodwork-title">
          {view === "stats" ? "Bloodwork" : view === "individual" ? "Individual assessment" : "Missing values report"}
        </h2>
        {view === "individual" ? (
          <>
            <input ref={individualInputRef} type="file" accept=".doc,.docx" style={{ display: "none" }} onChange={(e) => uploadIndividual(e.target.files?.[0])} />
            <button data-testid="bloodwork-individual-upload-btn" onClick={() => individualInputRef.current?.click()} disabled={individualLoading} style={{ minWidth: 104, height: 38, border: 0, borderRadius: 9, background: "#0f5132", color: "#fff", fontWeight: 800 }}>
              {individualLoading ? "Reading…" : "Upload"}
            </button>
          </>
        ) : (
          <select data-testid="bloodwork-month-filter" value={month} onChange={(e) => setMonth(e.target.value)} style={{ height: 38, minWidth: 150, border: `1px solid ${BORDER}`, borderRadius: 9, padding: "0 10px", background: "#fff", fontWeight: 700 }}>
            <option value="all">All months</option>
            {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>

      <div className="bw-subnav" data-testid="bloodwork-view-nav">
        <button data-testid="bloodwork-view-stats" className={view === "stats" ? "active" : ""} onClick={() => setView("stats")}>All statistics</button>
        <button data-testid="bloodwork-view-individual" className={view === "individual" ? "active" : ""} onClick={() => setView("individual")}>Individual result</button>
        <button data-testid="bloodwork-view-missing" className={view === "missing" ? "active" : ""} onClick={() => setView("missing")}>Missing values report</button>
      </div>

      {view === "stats" && (
        <>
          {isAdmin && (
            <section
              data-testid="bloodwork-bulk-upload-zone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); uploadBulk(e.dataTransfer.files); }}
              onClick={() => bulkInputRef.current?.click()}
              style={{ marginBottom: 14, minHeight: 58, border: "2px dashed #b8d4c8", borderRadius: 10, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: TEAL, fontSize: 11, fontWeight: 800, cursor: "pointer", textAlign: "center", padding: 10 }}
            >
              <input ref={bulkInputRef} type="file" accept=".doc,.docx" multiple style={{ display: "none" }} onChange={(e) => uploadBulk(e.target.files)} />
              <UploadCloud style={{ width: 15, height: 15, marginRight: 7 }} />
              Drag multiple reports here or tap to choose files
            </section>
          )}
          {bulkProgress && (
            <div data-testid="bloodwork-bulk-progress" style={{ ...CARD, padding: 12, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 800 }}>
                <span>Processing {bulkProgress.processed} of {bulkProgress.total} reports</span>
                <span>{bulkProgress.stored} added{bulkProgress.failed ? ` · ${bulkProgress.failed} failed` : ""}</span>
              </div>
              <div style={{ height: 7, borderRadius: 99, background: "#e8eef0", marginTop: 8, overflow: "hidden" }}>
                <div style={{ width: pctWidth((bulkProgress.processed / bulkProgress.total) * 100), height: "100%", background: TEAL }} />
              </div>
            </div>
          )}
          <div className="bw-metrics" data-testid="bloodwork-metrics-grid">
            <Metric label="Records assessed" value={summary?.records_assessed ?? "—"} note="Anonymous lab-value records" />
            <Metric label="Benchmark status" value={summary?.benchmark_active ? "Active" : "Collecting"} note={`First ${summary?.baseline_target || 100} records complete`} accent={summary?.benchmark_active} />
            <Metric label="Benchmark match" value={`${summary?.benchmark_match_pct ?? 0}%`} note="Only available values are scored" />
            <Metric label="Data completeness" value={`${summary?.data_completeness_pct ?? 0}%`} note="Values present across reports" />
          </div>
          <section style={CARD} data-testid="bloodwork-lab-values-table">
            <div style={{ padding: "15px 16px 10px", display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontFamily: "Outfit", fontSize: 15, fontWeight: 600 }}>Common lab-value ranges</h3>
                <p style={{ margin: "3px 0 0", color: MUTED, fontSize: 10.5 }}>Seven values tracked from every uploaded report · {month === "all" ? "All months" : month}</p>
              </div>
              {loading && <span style={{ color: MUTED, fontSize: 10 }}>Loading…</span>}
            </div>
            <div className="bw-table">
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                <thead><tr>{["Lab value","Available","Missing","Low","High","Average","Normal","Above normal"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {labs.map((lab) => (
                    <tr key={lab.key} data-testid={`bloodwork-lab-row-${lab.key}`}>
                      <td style={tdStyle}><b>{lab.label}</b><br /><span style={{ color: MUTED, fontSize: 9.5 }}>{lab.name} · {lab.unit}</span></td>
                      <td style={numStyle}>{lab.available}</td>
                      <td style={{ ...numStyle, color: lab.missing ? RED : MUTED, fontWeight: 800 }}>{lab.missing}</td>
                      <td style={numStyle}>{fmt(lab.low)}</td>
                      <td style={numStyle}>{fmt(lab.high)}</td>
                      <td style={numStyle}>{fmt(lab.average)}</td>
                      <td style={tdStyle}><Pill>{lab.normal_label}</Pill></td>
                      <td style={{ ...tdStyle, minWidth: 110 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800 }}><span>{lab.above_normal_pct}%</span><span>{lab.above_normal_count}/{lab.available}</span></div>
                        <div style={{ height: 6, borderRadius: 99, background: "#e8eef0", marginTop: 5 }}><div style={{ width: pctWidth(lab.above_normal_pct), height: "100%", background: RED, borderRadius: 99 }} /></div>
                        <div style={{ color: MUTED, fontSize: 9, marginTop: 4 }}>Normal {lab.normal_count}{lab.below_normal_count ? ` · Below ${lab.below_normal_count}` : ""}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {view === "individual" && (
        <section style={CARD} data-testid="bloodwork-individual-result-card">
          {!result ? (
            <div style={{ padding: 28, textAlign: "center", color: MUTED, fontSize: 12 }}>
              <Droplets style={{ width: 24, height: 24, color: TEAL, marginBottom: 8 }} />
              <div>Upload one report to assess it.</div>
            </div>
          ) : (
            <>
              <div data-testid="bloodwork-upload-qualification" style={{ margin: 14, padding: 14, borderRadius: 11, background: result.qualified ? "#eaf5ef" : "#fdf2f2", border: `1px solid ${result.qualified ? "#bfe4cf" : "#efc7c7"}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: result.qualified ? GREEN : RED, fontFamily: "Outfit", fontSize: 22, fontWeight: 700 }}>
                  {result.qualified ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                  {result.status_label}
                </div>
                <p style={{ margin: "5px 0 0", color: MUTED, fontSize: 11 }}>{result.reason}</p>
              </div>
              <div className="bw-marker-grid" style={{ padding: "0 14px 14px" }} data-testid="bloodwork-upload-checks">
                {result.markers.map((m) => (
                  <div key={m.key} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: 10 }} data-testid={`bloodwork-marker-${m.key}`}>
                    <div style={{ fontFamily: "Outfit", fontSize: 14, fontWeight: 800 }}>{m.label}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}>{m.value == null ? "Not reported" : `${m.value} ${m.unit}`}</div>
                    <div style={{ color: MUTED, fontSize: 9, lineHeight: 1.3 }}>Benchmark {m.benchmark_label}<br />Normal {m.normal_label}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
                      {[markerBadge(m.benchmark_status, "benchmark"), markerBadge(m.normal_status, "normal")].map((b, idx) => (
                        <span key={`${m.key}-${idx}`} style={{ borderRadius: 999, padding: "4px 6px", fontSize: 8.5, fontWeight: 800, color: b.color, background: b.bg }}>{b.label}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {view === "missing" && (
        <>
          <div className="bw-metrics" data-testid="bloodwork-missing-metrics">
            <Metric label="Overall availability" value={`${summary?.data_completeness_pct ?? 0}%`} note="Across all expected values" accent />
            <Metric label="Full panels" value={summary?.completeness?.full_panels ?? 0} note="Records with all 7 values" />
            <Metric label="Partial panels" value={summary?.completeness?.partial_panels ?? 0} note="At least 5 values available" />
            <Metric label="Minimal panels" value={summary?.completeness?.minimal_panels ?? 0} note="Staff review needed" />
          </div>
          <section style={CARD} data-testid="bloodwork-missing-coverage-table">
            <div style={{ padding: "15px 16px 10px" }}>
              <h3 style={{ margin: 0, fontFamily: "Outfit", fontSize: 15, fontWeight: 600 }}>Availability by lab value</h3>
            </div>
            <div className="bw-table">
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
                <thead><tr>{["Lab value","Available","Missing","Missing rate","Coverage"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {labs.map((lab) => (
                    <tr key={lab.key}>
                      <td style={tdStyle}><b>{lab.label}</b><br /><span style={{ color: MUTED, fontSize: 9.5 }}>{lab.name}</span></td>
                      <td style={numStyle}>{lab.available}</td>
                      <td style={numStyle}><span style={{ borderRadius: 999, padding: "4px 7px", background: "#fff7ed", color: AMBER, fontWeight: 800 }}>{lab.missing}</span></td>
                      <td style={numStyle}>{lab.missing_pct}%</td>
                      <td style={{ ...tdStyle, minWidth: 110 }}><div style={{ height: 6, borderRadius: 99, background: "#e8eef0" }}><div style={{ width: pctWidth(lab.coverage_pct), height: "100%", background: TEAL, borderRadius: 99 }} /></div><span style={{ color: MUTED, fontSize: 9.5 }}>{lab.coverage_pct}% available</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, note, accent = false }) {
  return (
    <div style={{ ...CARD, padding: 14, background: accent ? "#eaf5ef" : "#fff", borderColor: accent ? "#cde5d8" : BORDER }}>
      <div style={{ color: MUTED, fontSize: 10, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 6, fontFamily: "Outfit", fontSize: 24, lineHeight: 1, fontWeight: 700, color: accent ? GREEN : "#111827" }}>{value}</div>
      <div style={{ marginTop: 5, color: MUTED, fontSize: 10 }}>{note}</div>
    </div>
  );
}

function Pill({ children }) {
  return <span style={{ display: "inline-flex", borderRadius: 999, padding: "4px 6px", background: "#f2f6f7", color: "#183754", fontFamily: "monospace", fontSize: 9, fontWeight: 700 }}>{children}</span>;
}

const thStyle = { padding: "10px 8px", color: MUTED, fontSize: 9, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", textAlign: "left", borderBottom: `1px solid ${BORDER}` };
const tdStyle = { padding: "10px 8px", borderBottom: "1px solid #edf2f3", fontSize: 11 };
const numStyle = { ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700 };
function fmt(v) { return v == null ? "—" : v; }
